import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { DesktopHtsSidebarComponent, EntityUnifyDefaultDesktopComponent } from '@shared/isf-mutual/components';
import { AppService } from '@app/app.service';
import { CompanyModel } from '@data-layer/models/companies';
import { EntityModel } from '@data-layer/models/entities';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { CompaniesService } from '@services/companies.service';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { IsfStateService } from '@shared/isf-mutual/services';
import { IsfModel } from '@data-layer/models/isf';
import { DateHelper } from '@helpers/date.helper';
import { DESC_ENTITY } from '@constants/desc-entity';
import { DocumentsService } from '@services/documents.service';
import { DesktopBlSidebarComponent } from '../desktop-bl-sidebar/desktop-bl-sidebar.component';
import { DesktopShipmentTypeSidebarComponent } from '../desktop-shipment-type-sidebar/desktop-shipment-type-sidebar.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'isf-batch-review-sidebar',
  templateUrl: './isf-batch-review-sidebar.component.html',
  styleUrls: ['./isf-batch-review-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IsfBatchReviewSidebarComponent implements OnInit, OnDestroy {
  @Input() public referenceNumber: string;
  @Input() public isChecked: boolean;
  @Output() public review: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public validChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public reloadList: EventEmitter<void> = new EventEmitter<void>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();

  public isConfirmedFc: UntypedFormControl;

  public isLoading: boolean;

  public isf: IsfModel;

  public company: CompanyModel;
  public importer: EntityModel;
  public consignee: EntityModel;
  public buyer: EntityModel;
  public shipTo: EntityModel;
  public isImporterEdited: boolean;
  public isConsigneeEdited: boolean;
  public isBuyerEdited: boolean;
  public isShipToEdited: boolean;

  public seller: EntityModel;
  public consolidator: EntityModel;
  public staffingLocation: EntityModel;
  public isSellerEdited: boolean;
  public isConsolidatorEdited: boolean;
  public isStaffingLocationEdited: boolean;

  public manufacturers: EntityModel[];
  public invoices: InvoiceModel[];

  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private appService: AppService,
    private companiesService: CompaniesService,
    private isfStateService: IsfStateService,
    private cdr: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private translateService: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.isConfirmedFc = new UntypedFormControl(this.isChecked);
    this.isConfirmedFc.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isConfirmed: boolean) => this.review.emit(isConfirmed));

    this.company = this.companiesService.getCompany();

    this.loadData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.prepareData();
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isEditedEntity(entity: EntityModel): boolean {
    if (!this.isf || (!!this.isf && !this.isf.sentToProviderDate)) {
      return;
    }

    return DateHelper.isSameOrBefore(this.isf?.sentToProviderDate, entity?.modificationData?.modifiedDate);
  }

  public editEntity(data: { id: number; entityType: enumEntityModelCode }): void {
    const componentSidebarRef: ComponentRef<EntityUnifyDefaultDesktopComponent> = this.appService.openSidebar(
      EntityUnifyDefaultDesktopComponent,
      {
        sidebarHeaderText: this.translateService.instant('LABELS.ENTER_ENTITY_DETAILS', {
          entityTitle: this.translateService.instant(DESC_ENTITY[data.entityType]?.titleDesktop).toLowerCase(),
        }),
      },
    );

    componentSidebarRef.instance.info = DESC_ENTITY[data.entityType];
    componentSidebarRef.instance.entityId = data.id;
    this.onDataSaved(componentSidebarRef);
  }

  public onDeleteManufacturerHTS(params: { id: number; manufacturerId: number }): void {
    this.isfStateService
      .deleteLineItem(params.id, params.manufacturerId)
      .pipe(
        switchMap(() => this.loadData()),
        takeUntil(this.destroy$),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.prepareData(true);
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  public onDeleteManufacturer(manufacturerId: number): void {
    const invoiceId: number = (this.isfStateService.currentIsf?.shipment?.invoices || []).find(
      (item: InvoiceModel) => item.manufacturerId === manufacturerId,
    )?.id;

    this.isfStateService
      .deleteManufacturer(invoiceId)

      .pipe(
        switchMap(() => this.loadData()),
        takeUntil(this.destroy$),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.prepareData(true);
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  public editHTS(entityId?: number): void {
    const componentSidebarRef: ComponentRef<DesktopHtsSidebarComponent> = this.appService.openSidebar(DesktopHtsSidebarComponent, {
      sidebarHeaderText: 'LABELS.INTERNATIONAL_HTS_CODE',
    });

    componentSidebarRef.instance.entityId = entityId;
    this.onDataSaved(componentSidebarRef);
  }

  public editShipmentType(invoices: InvoiceModel[]): void {
    const componentSidebarRef: ComponentRef<DesktopShipmentTypeSidebarComponent> = this.appService.openSidebar(
      DesktopShipmentTypeSidebarComponent,
      {
        sidebarHeaderText: 'LABELS.SHIPMENT_TYPE',
      },
    );

    componentSidebarRef.instance.invoices = invoices;
    this.onDataSaved(componentSidebarRef);
  }

  public editBL(): void {
    const componentSidebarRef: ComponentRef<DesktopBlSidebarComponent> = this.appService.openSidebar(DesktopBlSidebarComponent, {
      sidebarHeaderText: 'LABELS.BILL_OF_LADING_DETAILS',
    });

    this.onDataSaved(componentSidebarRef);
  }

  public onDownloadAllFiles(documentsIds: number[]): void {
    this.documentsService
      .getArchive(this.isf.referenceNumber, documentsIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.isf.referenceNumber));
      });
  }

  public get isValidHTS(): boolean {
    return this.isf.shipment.invoices.length && this.isf.shipment.invoices.every((invoice: InvoiceModel) => invoice.lines.length);
  }

  private loadData(): Observable<IsfModel> {
    return this.isfStateService.getSummaryById(this.referenceNumber);
  }

  private prepareData(isEmitReload?: boolean): void {
    this.isfStateService.prepareIsfData(this);

    if (!this.isf.valid) {
      this.isConfirmedFc.setValue(false, { emitEvent: false });
      this.validChange.emit(false);
      this.isConfirmedFc.disable();
    } else {
      this.validChange.emit(true);
      this.isConfirmedFc.enable();
    }

    if (isEmitReload) {
      this.reloadList.emit();
    }
  }

  private onDataSaved(componentSidebarRef: ComponentRef<any>): void {
    componentSidebarRef.instance.dataSaved
      .pipe(
        switchMap(() => this.loadData()),
        takeUntil(this.destroy$),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.prepareData(true);
        this.appService.closeSidebar();
        this.cdr.markForCheck();
      });
  }
}
